"use client";

import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { AreaViewJumpSection } from "../area-view-types";

/**
 * The phone jump bar (G3 addendum 2): a sticky row of section chips directly under the hero that
 * sticks under the site header, scrolls to a section on tap and highlights the section in view
 * (scroll-spy with IntersectionObserver). Phones only (hidden from md). Plain anchors, so it works
 * before hydration and without JavaScript. Sections hidden by the sport toggle are skipped, and
 * chips that differ by sport carry `data-area-panel` (and `data-area-needs`), so the sport scope's
 * style rules show only the selected sport's chips.
 */

/** The site header (64px) plus this bar, so a section lands just under both. */
const STICKY_OFFSET = 124;

function visible(element: HTMLElement): boolean {
  return element.getClientRects().length > 0;
}

export function AreaJumpBar({ sections }: Readonly<{ sections: readonly AreaViewJumpSection[] }>) {
  const [active, setActive] = useState<string | null>(sections[0]?.id ?? null);
  const railRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef<number>(0);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const inView = new Map<string, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) inView.set(entry.target.id, entry.isIntersecting);
        if (performance.now() < lockRef.current) return;
        // About sits directly under the hero, above the first chip's section: while it is the
        // only section in view the reader is at the top, so the first chip is the active one.
        const first = sections[0];
        const firstElement = first ? document.getElementById(first.id) : null;
        const aboveFirst = (id: string) => {
          const element = document.getElementById(id);
          return Boolean(element && firstElement && element !== firstElement
            && element.compareDocumentPosition(firstElement) & Node.DOCUMENT_POSITION_FOLLOWING);
        };
        const current = sections.find((section) => inView.get(section.id) && !aboveFirst(section.id))
          ?? (sections.some((section) => inView.get(section.id)) ? first : undefined);
        if (current) setActive(current.id);
      },
      { rootMargin: `-${STICKY_OFFSET}px 0px -55% 0px`, threshold: 0 },
    );
    for (const section of sections) {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [sections]);

  // Keep the active chip inside the rail.
  useEffect(() => {
    const rail = railRef.current;
    const chip = active
      ? [...(rail?.querySelectorAll<HTMLElement>(`[data-jump="${active}"]`) ?? [])].find(visible) ?? null
      : null;
    if (!rail || !chip) return;
    const railBox = rail.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    if (chipBox.left < railBox.left + 16) rail.scrollLeft += chipBox.left - railBox.left - 16;
    else if (chipBox.right > railBox.right - 40) rail.scrollLeft += chipBox.right - railBox.right + 40;
  }, [active]);

  if (sections.length < 2) return null;

  const jump = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    const target = document.getElementById(id);
    if (!target || !visible(target)) return;
    event.preventDefault();
    const closedDetails = target.querySelector("details:not([open])");
    if (closedDetails) closedDetails.setAttribute("open", "");
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const top = target.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 4;
    // event.timeStamp shares performance.now()'s clock (the observer reads that).
    lockRef.current = event.timeStamp + (reduce ? 100 : 900);
    setActive(id);
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
    target.focus({ preventScroll: true });
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#${id}`);
  };

  return (
    <nav
      aria-label="Jump to section"
      data-area-jump=""
      className="sticky top-16 z-30 border-b border-border bg-warm-bg shadow-[0_8px_18px_rgba(22,35,59,0.10)] md:hidden"
    >
      <div
        ref={railRef}
        className="flex gap-1.5 overflow-x-auto px-4 py-2.5 [mask-image:linear-gradient(90deg,#000_86%,transparent)] [scroll-padding-left:16px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sections.map((section) => {
          const on = section.id === active;
          return (
            <a
              key={`${section.id}:${section.label}:${section.panels ?? ""}`}
              href={`#${section.id}`}
              data-jump={section.id}
              data-area-panel={section.panels}
              data-area-needs={section.needs}
              aria-current={on ? "location" : undefined}
              onClick={(event) => jump(event, section.id)}
              className={`inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full border px-3.5 text-[14px] no-underline transition-colors ${
                on ? "border-navy bg-navy font-bold text-white" : "border-border bg-white font-semibold text-navy hover:border-navy"
              }`}
            >
              {section.label}
            </a>
          );
        })}
        <span aria-hidden="true" className="w-6 shrink-0" />
      </div>
    </nav>
  );
}
